import { EvidenceType } from 'src/api';
import { GetReportByIdQuery } from 'src/graphql';
import { stripFileExtension } from 'src/utils';
import { DELETION_DATE_ONLY_REGEX } from 'src/utils/commonRegex';
import { DateFormats, formatDate, isValidDate } from 'src/utils/time';
import * as Types from '../../../../types';

export type ReportCardFullType = Omit<
  NonNullable<GetReportByIdQuery['getReportById']>,
  '__typename'
>;

export type FileUploadEvidenceContentType = {
  size: number | undefined;
  dateAdded: Date;
  status: Types.UploadScanStatus;
  fileS3Path: string;
  fileExtension: string;
  dataDeletedAt?: string | null;
};

export const getEvidenceContent = (
  report: ReportCardFullType,
  evidenceType: EvidenceType,
  activeTab?: string,
) => {
  const events = report.evidence?.events || [];
  const tabId = activeTab ?? (events.length === 1 ? events[0].id : undefined);
  const match = report.evidence?.evidence?.find(
    (evidence) =>
      evidence.type === evidenceType &&
      (evidence.event === tabId || evidence.id === tabId),
  );
  return match ? match.value : '';
};

export const getFormattedDate = (date: string) => {
  return isValidDate(date)
    ? formatDate(date, DateFormats.DD_MMM_YYYY).toUpperCase()
    : date;
};

export const getPerpetratorNames = (
  report: ReportCardFullType,
  activeTab?: string,
) => {
  if (report && report.perpetrators) {
    const events = report.evidence?.events || [];
    const tabId = activeTab ?? (events.length === 1 ? events[0].id : undefined);
    const tabEvidence =
      report.evidence?.evidence?.filter(
        (evidence) =>
          evidence.type === EvidenceType.PERPETRATOR &&
          (evidence.event === tabId || evidence.id === tabId),
      ) ?? [];
    const perpetratorIds = tabEvidence.map((evidence) => {
      if (evidence && evidence.value && 'name' in evidence.value) {
        return (evidence.value as { name: string }).name;
      }
      return null;
    });

    const filteredPerpetrators =
      events.length > 1
        ? report.perpetrators.filter((perpetrator) =>
            perpetratorIds.includes(perpetrator.name),
          )
        : report.perpetrators;

    return `${filteredPerpetrators
      .map((perpetrator) => {
        if (
          DELETION_DATE_ONLY_REGEX.test(perpetrator.name.toLowerCase()) ||
          perpetrator.dataDeletedAt
        ) {
          let deletedDate;

          if (perpetrator.dataDeletedAt) {
            deletedDate = getFormattedDate(perpetrator.dataDeletedAt);
          } else if (
            DELETION_DATE_ONLY_REGEX.test(perpetrator.name.toLowerCase())
          ) {
            const match = perpetrator.name.match(/\[deleted\s+(.+?)\]/);
            if (match && match[1]) {
              deletedDate = match[1];
            }
          }
          return `[Deleted ${deletedDate}]\n`;
        }
        return `${perpetrator.name}\n`;
      })
      .join(', ')}`;
  }
  return '';
};

export const getFileUploadEvidenceContent = (
  report: ReportCardFullType,
  activeTab?: string,
): FileUploadEvidenceContentType[] => {
  const events = report.evidence.events || [];
  const tabId = activeTab ?? (events.length === 1 ? events[0].id : undefined);
  const result =
    report && report.evidence && report.evidence.evidence
      ? report.evidence.evidence
          .filter(
            (evidence) =>
              evidence.type === EvidenceType.PHOTO &&
              (tabId
                ? evidence.event === tabId || evidence.id === tabId
                : true),
          )
          .map((evidence) => {
            const splitS3Path = evidence.value.path?.split('.') ?? ['', ''];

            const value = evidence.value;
            return {
              size: value.fileSize ?? undefined,
              dateAdded: new Date(evidence.timestamp),
              status: (value.scanStatus ??
                Types.UploadScanStatus.PENDING) as Types.UploadScanStatus,
              fileS3Path: stripFileExtension(evidence.value.path),
              fileExtension:
                evidence.value.fileExtension ?? splitS3Path.pop() ?? '',
              dataDeletedAt: value.dataDeletedAt ?? null,
            };
          })
      : [];

  return result;
};
